import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import {
  listEventLogs,
  type ListEventLogsParams,
} from 'api/admin/eventLogs/listEventLogs'
import { eventLogsKeys } from './keys'

type UseEventLogsQueryData = Awaited<ReturnType<typeof listEventLogs>>
type UseEventLogsQueryKey = ReturnType<typeof eventLogsKeys.list>
type UseEventLogsQueryPageParam = string | undefined

type UseEventLogsQueryOptions = Partial<
  UseInfiniteQueryOptions<
    UseEventLogsQueryData,
    Error,
    InfiniteData<UseEventLogsQueryData, UseEventLogsQueryPageParam>,
    UseEventLogsQueryData,
    UseEventLogsQueryKey,
    UseEventLogsQueryPageParam
  >
>

export function useEventLogsQuery(
  baseParams: Omit<ListEventLogsParams, 'cursor'> | null,
  options: UseEventLogsQueryOptions = {}
) {
  return useInfiniteQuery<
    UseEventLogsQueryData,
    Error,
    InfiniteData<UseEventLogsQueryData, UseEventLogsQueryPageParam>,
    UseEventLogsQueryKey,
    UseEventLogsQueryPageParam
  >({
    ...options,
    enabled: baseParams !== null && (options.enabled ?? true),
    queryKey: eventLogsKeys.list(baseParams),
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => {
      if (!baseParams) {
        throw new Error('useEventLogsQuery: queryFn ran while disabled')
      }
      return listEventLogs({
        ...baseParams,
        ...(pageParam ? { cursor: pageParam } : {}),
      })
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
}
